import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/AuthProvider';
import Spinner from '../../components/spinner';
import { request } from '../../services/request';
import ThemeToggle from '../../components/ThemeToggle';

const OAuthConsent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [scopeDescriptions, setScopeDescriptions] = useState([]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get('client_id');
  const redirectUri = queryParams.get('redirect_uri');
  const responseType = queryParams.get('response_type');
  const scope = queryParams.get('scope') || 'profile';
  const state = queryParams.get('state');

  // Map OAuth scopes to human-readable descriptions
  const getScopeDescriptions = (scopeString) => {
    const scopes = scopeString.split(' ');
    const descriptions = [];
    
    if (scopes.includes('profile')) {
      descriptions.push({
        scope: 'profile',
        title: 'View your profile information',
        description: 'Access to your basic profile information such as name and username.'
      });
    }
    
    if (scopes.includes('email')) {
      descriptions.push({
        scope: 'email',
        title: 'View your email address',
        description: 'Access to your email address.'
      });
    }
    
    return descriptions;
  };

  // Fetch client information on component mount
  useEffect(() => {
    const fetchClientInfo = async () => {
      if (!clientId || !redirectUri || !responseType) {
        setError('Missing required OAuth parameters');
        setLoading(false);
        return;
      }

      try {
        const authorizeUrl = `/api/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=${responseType}&scope=${scope}${state ? `&state=${state}` : ''}`;
        const data = await request(authorizeUrl);
        
        setClientInfo(data.client);
        setScopeDescriptions(getScopeDescriptions(data.scope));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching client info:', err);
        setError('Failed to load application details. Please try again.');
        setLoading(false);
      }
    };

    // Redirect to login page if the user is not authenticated
    if (!currentUser) {
      const loginParams = new URLSearchParams(location.search);
      navigate(`/oauth/login?${loginParams.toString()}`);
      return;
    }

    fetchClientInfo();
  }, [clientId, redirectUri, responseType, scope, state, currentUser, location.search, navigate]);

  // Handle user's decision (approve or deny)
  const handleDecision = async (approved) => {
    setLoading(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('approved', approved.toString());
      
      // The authorization endpoint will handle the redirect
      window.location.href = `/api/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=${responseType}&scope=${scope}${state ? `&state=${state}` : ''}&approved=${approved}`;
    } catch (err) {
      console.error('Authorization error:', err);
      setError('Failed to process your decision. Please try again.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ThemeToggle compact className="fixed right-4 top-4" />
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ThemeToggle compact className="fixed right-4 top-4" />
        <div className="bg-white dark:bg-gray-900 p-8 rounded-lg shadow max-w-md w-full">
          <h2 className="text-red-600 text-xl font-semibold mb-4">Error</h2>
          <p className="text-gray-700 dark:text-gray-300">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <ThemeToggle compact className="fixed right-4 top-4" />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-gray-50">
          Authorize Application
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-900 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">
              {clientInfo?.name || 'Application'} would like to:
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {clientInfo?.description || 'Access your account'}
            </p>
          </div>

          <div className="mb-6">
            <ul className="space-y-4">
              {scopeDescriptions.map((scopeItem) => (
                <li key={scopeItem.scope} className="flex">
                  <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50">{scopeItem.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{scopeItem.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md mb-6">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Logged in as <strong>{currentUser?.email}</strong>
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => handleDecision(false)}
              disabled={loading}
              className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-800 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDecision(true)}
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-indigo-500"
            >
              {loading ? <Spinner size="sm" /> : 'Authorize'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthConsent;